import * as React from "react";
import { cn } from "@babackapp/customer-ui/lib/utils";

interface CustomerUiProviderProps {
  children?: React.ReactNode;
  className?: string;
  colors?: {
    primary?: string;
  };
  fonts?: {
    sans?: string;
  };
  customCssUrl?: string;
}

export function CustomerUiProvider({ children, className, colors, fonts, customCssUrl }: CustomerUiProviderProps) {
  // Inject shop-level theme overrides as an unlayered stylesheet targeting `.cui`,
  // the same selector globals.css uses for the design tokens. A layered rule would
  // lose to that unlayered rule whatever its specificity. Appended before the
  // merchant custom CSS link, so that one still wins on source order.
  const shopStyles = buildShopStyles({ colors, fonts });

  React.useEffect(() => {
    if (!shopStyles) return;
    const style = document.createElement("style");
    style.setAttribute("data-baback-shop-theme", "");
    style.textContent = shopStyles;
    // The storefront font arrives after the custom CSS link is mounted: an
    // append would land after it and override the merchant's font.
    document.head.insertBefore(style, document.head.querySelector("link[data-baback-custom-css]"));
    return () => style.remove();
  }, [shopStyles]);

  React.useEffect(() => {
    if (!customCssUrl) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = customCssUrl;
    link.setAttribute("data-baback-custom-css", "");
    document.head.appendChild(link);
    return () => link.remove();
  }, [customCssUrl]);

  return (
    <div className={cn("cui", className)}>
      {children}
    </div>
  );
}

function buildShopStyles({ colors, fonts }: Pick<CustomerUiProviderProps, "colors" | "fonts">): string | null {
  const vars: string[] = [];
  if (colors?.primary) vars.push(`--primary: ${colors.primary};`);
  if (fonts?.sans) vars.push(`--font-sans: ${fonts.sans}, "Poppins", sans-serif;`);
  if (vars.length === 0) return null;
  return `.cui, .cui.dark { ${vars.join(" ")} }`;
}
